import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './header2.scss';
import { useResizeListener } from './useResizeListener';

type HeaderProps = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    left?: ReactNode;
    right?: ReactNode;
    dataTestId?: string;
    hideLabelsOnOverflow?: boolean;
  };

const COMPONENT_ID = 'modulor-header2';

export const Header2: ForwardRefExoticComponent<HeaderProps> = forwardRef<
  HTMLDivElement,
  HeaderProps
>(
  (
    {
      style,
      left,
      right,
      hideLabelsOnOverflow,
      dataTestId = COMPONENT_ID,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const [itemsOverflow, setItemsOverflow] = useState(false);
    const [itemsLoading, setItemsLoading] = useState(true);
    const itemsRef = useRef<HTMLDivElement | null>(null);

    const updateMenu = useCallback(() => {
      if (!itemsRef.current || !hideLabelsOnOverflow) {
        setItemsOverflow(false);
        setItemsLoading(false);
        return;
      }

      const { scrollWidth, clientWidth } = itemsRef.current;
      const newOverflow = !itemsOverflow ? clientWidth < scrollWidth : clientWidth === scrollWidth;

      setItemsOverflow(newOverflow);
      setItemsLoading(false);
    }, [hideLabelsOnOverflow]);

    useEffect(() => {
      if (!hideLabelsOnOverflow) {
        setItemsLoading(false);
        return;
      }

      requestAnimationFrame(updateMenu);
    }, [hideLabelsOnOverflow, children, updateMenu]);

    useEffect(() => {
      updateMenu();
    }, [children]);

    useResizeListener(hideLabelsOnOverflow, updateMenu);

    return (
      <div
        className={classNames(className, styles.container)}
        ref={ref}
        {...rest}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}>
        <div style={style} className={styles.header}>
          {left && (
            <div className={styles.left} data-testid={`${dataTestId}-left`}>
              {left}
            </div>
          )}
          <div
            style={{
              visibility: itemsLoading ? 'hidden' : 'visible',
            }}
            className={classNames(styles.items, {
              [styles['items-overflow']]: itemsOverflow,
            })}
            data-testid={`${dataTestId}-items`}
            ref={itemsRef}>
            {children}
          </div>
          {right !== null && (
            <div className={styles.right} data-testid={`${dataTestId}-right`}>
              {right}
            </div>
          )}
        </div>
      </div>
    );
  },
);

Header2.displayName = 'Header2';
